/**
 * ProfileEntrepriseActivityContextController — bloc *Contexte d'activité*
 * §2.F (EPIC-054 / 54.6) sur la page Profil entreprise, onglet Activités.
 *
 * Pilote les deux cases organization-level :
 *  - `isFormationProvider` — déclenche le trigger §19.C.2 *« formation_nda_declaration »*
 *  - `isSubjectToRep`       — déclenche le trigger *« rep_eco_organism_renewal »*
 *
 * Pré-cochage suggéré (non bloquant, D12) : un hint apparaît sous la case
 * si au moins une activité active de l'org a un code NAF compatible. La
 * case reste **décochée** par défaut — l'utilisateur valide explicitement.
 *
 * API :
 *  - `GET  /organization/profile`             → `activityContext`
 *  - `GET  /activities`                       → codes NAF (suggestions)
 *  - `PUT  /organization/profile/activity-context`
 *
 * Convention "pas de modal", pas de HTML inline en JS.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const FORMATION_NAF_PREFIXES = ["85"];
const REP_NAF_PREFIXES = ["47", "46"];

export default class ProfileEntrepriseActivityContextController extends BaseController {
    static targets = [
        "block", "summary", "submitBtn", "error",
        "formationInput", "formationHint",
        "repInput", "repHint",
    ];

    connect() {
        this.state = { isFormationProvider: false, isSubjectToRep: false };
        this.refresh();
    }

    async refresh() {
        try {
            const [profile, activities] = await Promise.all([
                api.get("/organization/profile"),
                api.get("/activities").catch(() => ({ activities: [] })),
            ]);
            const ctx = profile?.activityContext ?? {};
            this.state = {
                isFormationProvider: !!ctx.isFormationProvider,
                isSubjectToRep: !!ctx.isSubjectToRep,
            };
            this.renderSummary();
            this.restoreInputs();
            this.refreshHints(Array.isArray(activities?.activities) ? activities.activities : []);
        } catch {
            this.summaryTarget.textContent = "Impossible de charger le contexte d'activité.";
        }
    }

    renderSummary() {
        const parts = [];
        if (this.state.isFormationProvider) parts.push("Activité de formation professionnelle");
        if (this.state.isSubjectToRep) parts.push("Soumis à une filière REP");
        this.summaryTarget.textContent = parts.length === 0
            ? "Aucune option cochée."
            : parts.join(" · ");
    }

    restoreInputs() {
        this.formationInputTarget.checked = this.state.isFormationProvider;
        this.repInputTarget.checked = this.state.isSubjectToRep;
    }

    refreshHints(activities) {
        const activeNafs = activities
            .filter((a) => a.status === "active")
            .map((a) => (a.nafCode ?? "").toUpperCase());

        const suggestsFormation = activeNafs.some(
            (n) => FORMATION_NAF_PREFIXES.some((p) => n.startsWith(p)),
        );
        const suggestsRep = activeNafs.some(
            (n) => REP_NAF_PREFIXES.some((p) => n.startsWith(p)),
        );

        this.formationHintTarget.hidden = !(suggestsFormation && !this.state.isFormationProvider);
        this.repHintTarget.hidden = !(suggestsRep && !this.state.isSubjectToRep);
    }

    open(event) {
        event?.preventDefault();
        const block = this.blockTarget;
        block.dataset.editState = "editing";
        const form = block.querySelector("[data-edit-form]");
        const summary = block.querySelector("[data-edit-summary]");
        const openBtn = block.querySelector("[data-edit-open]");
        if (form) form.hidden = false;
        if (summary) summary.hidden = true;
        if (openBtn) openBtn.hidden = true;
    }

    cancel(event) {
        event?.preventDefault();
        this.restoreInputs();
        this.errorTarget.hidden = true;
        this.close();
    }

    close() {
        const block = this.blockTarget;
        block.dataset.editState = "idle";
        const form = block.querySelector("[data-edit-form]");
        const summary = block.querySelector("[data-edit-summary]");
        const openBtn = block.querySelector("[data-edit-open]");
        if (form) form.hidden = true;
        if (summary) summary.hidden = false;
        if (openBtn) openBtn.hidden = false;
    }

    async submit(event) {
        event.preventDefault();
        this.errorTarget.hidden = true;
        this.submitBtnTarget.disabled = true;

        const payload = {
            isFormationProvider: this.formationInputTarget.checked,
            isSubjectToRep: this.repInputTarget.checked,
        };
        try {
            const response = await api.put("/organization/profile/activity-context", payload);
            const ctx = response?.activityContext ?? payload;
            this.state = {
                isFormationProvider: !!ctx.isFormationProvider,
                isSubjectToRep: !!ctx.isSubjectToRep,
            };
            this.renderSummary();
            this.restoreInputs();
            this.refreshHints([]);
            this.close();
            this.showSuccess("Contexte d'activité enregistré.");
        } catch (err) {
            this.errorTarget.textContent = err?.message
                ?? "Erreur lors de la mise à jour du contexte d'activité.";
            this.errorTarget.hidden = false;
        } finally {
            this.submitBtnTarget.disabled = false;
        }
    }
}
